import React from "react";
import { cn } from "@/lib/utils";
import { Users } from "lucide-react";

function renderCard(node, onSelect, depth) {
  const initials = (node.name || "?")
    .split(" ")
    .map((s) => s[0])
    .slice(0, 2)
    .join("")
    .toUpperCase();

  return React.createElement(
    "button",
    {
      type: "button",
      onClick: () => onSelect && onSelect(node),
      className: cn(
        "min-w-[200px] max-w-[240px] text-left bg-white border border-slate-200 rounded-lg p-3 hover:border-slate-400 hover:shadow-sm transition relative",
        depth === 0 && "border-slate-900 shadow-md"
      ),
    },
    React.createElement(
      "div",
      { className: "flex items-center gap-2.5" },
      React.createElement(
        "div",
        { className: "w-9 h-9 rounded-full bg-slate-900 text-white flex items-center justify-center text-xs font-semibold" },
        initials
      ),
      React.createElement(
        "div",
        { className: "min-w-0 flex-1" },
        React.createElement(
          "div",
          { className: "font-display text-sm font-semibold text-slate-900 truncate" },
          node.name
        ),
        React.createElement(
          "div",
          { className: "text-[11px] text-slate-500 truncate" },
          node.designation || "—"
        )
      )
    ),
    React.createElement(
      "div",
      { className: "flex items-center justify-between mt-2 pt-2 border-t border-slate-100" },
      React.createElement(
        "span",
        { className: "text-[10px] uppercase tracking-wider text-slate-400" },
        node.department || "—"
      ),
      node.direct_report_count > 0
        ? React.createElement(
            "span",
            { className: "inline-flex items-center gap-1 text-[10px] text-slate-600" },
            React.createElement(Users, { className: "w-3 h-3" }),
            " ",
            node.direct_report_count
          )
        : null
    )
  );
}

function renderNode(node, onSelect, depth) {
  const hasChildren = node.children && node.children.length > 0;
  const childElements = hasChildren
    ? node.children.map((c) =>
        React.createElement(
          "div",
          { key: c.id, className: "flex flex-col items-center" },
          node.children.length > 1 ? React.createElement("div", { className: "w-px h-5 -mt-5 bg-slate-300" }) : null,
          renderNode(c, onSelect, depth + 1)
        )
      )
    : null;

  return React.createElement(
    "div",
    {
      className: "flex flex-col items-center",
      "data-testid": `org-node-${node.employee_id}`,
    },
    renderCard(node, onSelect, depth),
    hasChildren
      ? React.createElement(
          React.Fragment,
          null,
          React.createElement("div", { className: "w-px h-5 bg-slate-300" }),
          React.createElement(
            "div",
            { className: "relative flex items-start gap-6 pt-5" },
            node.children.length > 1
              ? React.createElement("div", { className: "absolute top-0 left-0 right-0 h-px bg-slate-300" })
              : null,
            ...childElements
          )
        )
      : null
  );
}

export default function OrgTreeView({ roots, onSelect }) {
  if (!roots || roots.length === 0) {
    return <div className="text-sm text-slate-500">No employees yet.</div>;
  }
  return (
    <div
      className="overflow-auto p-8 axia-grid-bg rounded-lg border border-slate-200 bg-white"
      data-testid="org-tree-canvas"
    >
      <div className="inline-flex gap-12">
        {roots.map((r) => (
          <React.Fragment key={r.id}>{renderNode(r, onSelect, 0)}</React.Fragment>
        ))}
      </div>
    </div>
  );
}
